import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, MessageCircle, XCircle } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { API, inr, useApi, waLink } from "@/lib/api";
import { fbEvent } from "@/lib/consent";
import { useSite } from "@/context/SiteContext";
import { Btn, Spinner } from "@/components/site/kit";

const Shell = ({ children, testId }) => (
  <section className="section bg-page"><div className="container-x"><div className="card-base mx-auto max-w-xl p-8 text-center sm:p-12" data-testid={testId}>{children}</div></div></section>
);

const Row = ({ k, v, testId }) => <div className="flex justify-between gap-4 border-b border-line py-2.5 text-sm last:border-0"><span className="text-muted2">{k}</span><span className="text-right font-semibold text-navy" data-testid={testId}>{v}</span></div>;

export function PaymentSuccess() {
  useSeo({ title: "Payment Successful" });
  const [params] = useSearchParams();
  const id = params.get("order");
  const { data: o, isLoading } = useApi(`/orders/${id}`, !!id);

  useEffect(() => {
    if (o?.status === "paid" && !sessionStorage.getItem(`purchase_${o.order_id}`)) {
      sessionStorage.setItem(`purchase_${o.order_id}`, "1");
      fbEvent("Purchase", { value: o.total_amount, currency: "INR", content_name: o.item?.name });
    }
  }, [o]);

  if (isLoading) return <Spinner className="min-h-[60vh]" />;
  return (
    <Shell testId="payment-success-page">
      <CheckCircle2 className="mx-auto h-16 w-16 text-success" />
      <h1 className="mt-5 text-3xl font-extrabold">Payment successful</h1>
      <p className="mt-3 text-body">Thank you{o?.customer?.name ? `, ${o.customer.name}` : ""}! Your access details will be sent to your email and WhatsApp shortly.</p>
      {o && (
        <div className="mt-8 rounded-xl bg-page p-5 text-left">
          <Row k="Order ID" v={o.order_id} testId="success-order-id" />
          <Row k="Item" v={o.item?.name} />
          <Row k="Amount paid" v={inr(o.total_amount)} testId="success-amount" />
          <Row k="Payment ID" v={o.payment_id || "-"} />
          <Row k="Status" v={o.status} testId="success-status" />
        </div>
      )}
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        {o?.status === "paid" && <Btn href={`${API}/orders/${o.order_id}/invoice`} arrow={false} testId="download-invoice-btn"><Download className="h-4 w-4" />Download Invoice</Btn>}
        <Btn to="/" variant="secondary" testId="success-home-btn">Back to Home</Btn>
      </div>
    </Shell>
  );
}

export function PaymentFailed() {
  useSeo({ title: "Payment Failed" });
  const { settings } = useSite();
  const [params] = useSearchParams();
  const id = params.get("order");
  const reason = params.get("reason");
  return (
    <Shell testId="payment-failed-page">
      <XCircle className="mx-auto h-16 w-16 text-red-500" />
      <h1 className="mt-5 text-3xl font-extrabold">Payment failed</h1>
      <p className="mt-3 text-body">{reason || "Your payment could not be completed."} If any amount was debited, it will be auto-refunded by your bank within 5–7 working days.</p>
      {id && <p className="mt-4 text-sm text-muted2">Order reference: <b className="text-navy" data-testid="failed-order-id">{id}</b></p>}
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Btn to="/pricing" testId="failed-retry-btn">Try Again</Btn>
        <Btn href={waLink(settings?.whatsapp, `Hi, my payment failed for order ${id || ""}`)} variant="secondary" arrow={false} testId="failed-whatsapp-btn"><MessageCircle className="h-4 w-4" />Get Help</Btn>
      </div>
      <Link to="/refund-policy" className="mt-6 inline-block text-sm font-semibold text-ocean hover:underline">Read our refund policy</Link>
    </Shell>
  );
}
